import { publish } from "./events";

// Evidence-based recovery for the webview's `isOnline` belief.
//
// That belief comes from the native shell forwarding React Native NetInfo's
// `isInternetReachable`, which is tri-state: true, false, or null for "not
// determined". Refusing to treat `null` as offline (see usePostMessages'
// onConnectionStatusChange) stops a healthy session being flipped, but it cannot
// RECOVER one that went offline for real and then reconnected to a network that
// blackholes NetInfo's probe — hospital wifi, captive portals, VPNs. NetInfo only
// emits on CHANGE, so no further true/false ever arrives and the session stays
// convinced it is offline while every one of our own API calls succeeds.
//
// That mattered because `isOnline` gates the transcript save: a stuck-offline
// session silently defers every recording. A completed HTTP response is direct
// evidence of connectivity and outranks the probe's opinion, so it is what we act
// on here.
export const ONLINE_EVIDENCE_EVENT = "gotOnlineEvidence";

/**
 * Announces that a request completed, i.e. the network demonstrably works.
 *
 * Fired from the axios success interceptor, so it must be inert on failure: a
 * throw here would turn a good API response into a rejected request. The event
 * carries no payload — the fact of the response IS the payload.
 */
export function publishOnlineEvidence() {
  try {
    publish(ONLINE_EVIDENCE_EVENT);
  } catch {
    // A missing/exotic document (SSR, teardown) must never break a request.
  }
}

/**
 * The `isOnline` value implied by that evidence, given the current belief.
 *
 * Returns the previous value UNCHANGED when it is already `true`, so the React
 * setState this feeds bails out instead of re-rendering the auth provider on
 * every single API response.
 */
export function resolveIsOnlineFromEvidence(previousIsOnline) {
  return previousIsOnline === true ? previousIsOnline : true;
}
